/**
 * Analytics Composable
 *
 * Provides a clean API for tracking analytics events.
 * Reads data from Pinia stores and constructs event payloads.
 *
 * Usage:
 *   const analytics = useAnalytics();
 *   analytics.trackSrpViewed();
 */

import { useProductListFiltersStore } from '@/stores/productListFilters';
import { useOfferFiltersStore } from '@/stores/offerFilters';
import { useCheckoutStore } from '@/stores/checkout';
import type { AnalyticsParameters, AnalyticsItem } from '@/services/analytics/types';
import {
  createViewItemListEvent,
  createViewItemEvent,
  createBookingCheckSuccessEvent,
  createBookingCheckFailEvent,
  createBeginCheckoutEvent,
  createCheckoutInsuranceEvent,
  createCheckoutDataEvent,
  createCheckoutPaymentEvent,
  createAddPaymentInfoEvent,
  createPurchaseEvent,
  createBookingErrorEvent,
  createHolidaySelectedEvent,
  pushEvent,
  pushEventBeforeRedirect,
} from '@/services/analytics/events';
import type { EventCallback } from '@/services/analytics/dataLayer';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import type { DateRange } from '@/types/SearchBar';
import { nightsInRange } from '@/utils/filters/durationMode';

/**
 * Local calendar day for GA4 date dimensions — toISOString() would report the
 * previous day for CET/CEST since store dates are local-midnight Dates.
 */
function localDateOrUndefined(date: Date | null | undefined): string | undefined {
  return date ? formatDateLocal(date) : undefined;
}

export function useAnalytics() {
  // ------------------------------------------
  // Parameter Builders (read from active stores)
  // ------------------------------------------

  /**
   * Build AnalyticsParameters from productListFiltersStore (SRP context)
   */
  function buildSrpParameters(): AnalyticsParameters {
    const store = useProductListFiltersStore();

    let adults = 0;
    let children = 0;
    store.roomOccupancy.rooms.forEach((room) => {
      room.persons.forEach((p) => {
        if (p.type === 'adult') adults++;
        else children++;
      });
    });

    return {
      destination: store.selectedCountries[0] ?? undefined,
      date_from: localDateOrUndefined(store.dateRange?.[0]),
      date_to: localDateOrUndefined(store.dateRange?.[1]),
      duration: store.duration,
      adults: adults || undefined,
      children: children || undefined,
      rooms: store.roomOccupancy.rooms.length || undefined,
    };
  }

  /**
   * Build AnalyticsParameters from offerFiltersStore (PDP context)
   */
  function buildPdpParameters(): AnalyticsParameters {
    const store = useOfferFiltersStore();

    let adults = 0;
    let children = 0;
    store.roomOccupancy.rooms.forEach((room) => {
      room.persons.forEach((p) => {
        if (p.type === 'adult') adults++;
        else children++;
      });
    });

    return {
      destination: undefined,
      date_from: localDateOrUndefined(store.dateRange?.[0]),
      date_to: localDateOrUndefined(store.dateRange?.[1]),
      duration: store.duration,
      adults: adults || undefined,
      children: children || undefined,
      rooms: store.roomOccupancy.rooms.length || undefined,
    };
  }

  /**
   * Build AnalyticsParameters from checkoutStore (checkout context)
   */
  function buildCheckoutParameters(): AnalyticsParameters {
    const store = useCheckoutStore();

    return {
      destination: undefined,
      date_from: localDateOrUndefined(store.checkIn),
      date_to: localDateOrUndefined(store.checkOut),
      duration: store.nightCount || undefined,
      adults: store.adultCount || undefined,
      // Infants travel and must count toward the passenger total; GA4 has no separate
      // infant dimension, so fold them into `children` (keeps adults + children = total,
      // matching pre-classification behaviour).
      children: store.childCount + store.infantCount || undefined,
      rooms: store.totalRooms || undefined,
      board_code: store.boardCode ?? undefined,
    };
  }

  /**
   * Build AnalyticsItem from checkout store (single product)
   */
  function buildCheckoutItem(): AnalyticsItem {
    const store = useCheckoutStore();
    return {
      item_id: store.productCode ?? 'unknown',
      item_name: store.productName ?? store.productCode ?? 'unknown',
      price: store.totalBookingPrice || undefined,
      quantity: 1,
    };
  }

  /**
   * Derive booking_warning from checkout availability state.
   */
  function getBookingWarning(): string | undefined {
    const store = useCheckoutStore();
    if (store.requiresRequestBooking) return 'request_booking';
    if (store.requiresConfirmation) return 'confirmation_required';
    return undefined;
  }

  // ------------------------------------------
  // Public Tracking Methods
  // ------------------------------------------

  /**
   * Event 1: SRP Viewed -> view_item_list
   */
  function trackSrpViewed(): void {
    const params = buildSrpParameters();
    pushEvent(createViewItemListEvent(params));
  }

  /**
   * Event 2: PDP Viewed -> view_item
   */
  function trackPdpViewed(productCode: string, productName?: string): void {
    const params = buildPdpParameters();
    const items: AnalyticsItem[] = [
      {
        item_id: productCode,
        item_name: productName ?? productCode,
        quantity: 1,
      },
    ];
    pushEvent(createViewItemEvent(params, items));
  }

  /**
   * Event 5a: DaVinci BA Check Success -> booking_check_success
   */
  function trackBookingCheckSuccess(
    davinciCode: number,
    sessionId: string | null,
    context: 'pdp' | 'checkout'
  ): void {
    const params = context === 'pdp' ? buildPdpParameters() : buildCheckoutParameters();
    const bookingWarning = context === 'checkout' ? getBookingWarning() : undefined;
    pushEvent(
      createBookingCheckSuccessEvent({
        session: sessionId,
        params,
        davinciCode,
        bookingWarning,
      })
    );
  }

  /**
   * Event 5b: DaVinci BA Check Fail -> booking_check_fail
   */
  function trackBookingCheckFail(
    davinciCode: number,
    errorText: string,
    sessionId: string | null,
    context: 'pdp' | 'checkout'
  ): void {
    const params = context === 'pdp' ? buildPdpParameters() : buildCheckoutParameters();
    const bookingWarning = context === 'checkout' ? getBookingWarning() : undefined;
    pushEvent(
      createBookingCheckFailEvent({
        session: sessionId,
        params,
        davinciCode,
        errorText,
        bookingWarning,
      })
    );
  }

  /**
   * Event 6: Checkout Entered -> begin_checkout
   */
  function trackCheckoutEntered(): void {
    const store = useCheckoutStore();
    pushEvent(
      createBeginCheckoutEvent({
        session: store.sessionId,
        params: buildCheckoutParameters(),
        currency: 'EUR',
        value: store.totalBookingPrice,
        bookingWarning: getBookingWarning(),
        items: [buildCheckoutItem()],
      })
    );
  }

  /**
   * Event 7: Insurance Step -> checkout_2_insurance
   */
  function trackCheckoutInsuranceStep(): void {
    const store = useCheckoutStore();
    pushEvent(
      createCheckoutInsuranceEvent({
        session: store.sessionId,
        productId: store.productCode,
        params: buildCheckoutParameters(),
      })
    );
  }

  /**
   * Event 8: Data Entry Step -> checkout_3_data
   */
  function trackCheckoutDataStep(): void {
    const store = useCheckoutStore();
    pushEvent(
      createCheckoutDataEvent({
        session: store.sessionId,
        productId: store.productCode,
        params: buildCheckoutParameters(),
      })
    );
  }

  /**
   * Event 9: Payment Step -> checkout_4_payment
   */
  function trackCheckoutPaymentStep(): void {
    const store = useCheckoutStore();
    pushEvent(
      createCheckoutPaymentEvent({
        session: store.sessionId,
        productId: store.productCode,
        params: buildCheckoutParameters(),
        bookingWarning: getBookingWarning(),
      })
    );
  }

  /**
   * Event 10: Payment Info Added -> add_payment_info
   * Fires together with purchase, right before redirect.
   * payment_type reflects the actual payment method (currently only bank_transfer).
   */
  function trackAddPaymentInfo(): void {
    const store = useCheckoutStore();
    pushEvent(
      createAddPaymentInfoEvent({
        session: store.sessionId,
        paymentType: 'bank_transfer',
        productId: store.productCode,
        params: buildCheckoutParameters(),
      })
    );
  }

  /**
   * Event 11: Purchase -> purchase
   * Fires add_payment_info first, then purchase with eventCallback redirect.
   */
  function trackPurchaseBeforeRedirect(
    transactionId: string,
    redirectCallback: EventCallback
  ): void {
    // Fire add_payment_info synchronously before purchase
    trackAddPaymentInfo();

    const store = useCheckoutStore();
    const event = createPurchaseEvent({
      session: store.sessionId,
      currency: 'EUR',
      value: store.totalBookingPrice,
      params: buildCheckoutParameters(),
      bookingWarning: getBookingWarning(),
      transactionId,
      items: [buildCheckoutItem()],
    });
    pushEventBeforeRedirect(event, redirectCallback, 2000);
  }

  /**
   * Event 12: Booking Error -> booking_error
   * Fires add_payment_info first (user was on payment step), then booking_error with eventCallback redirect.
   */
  function trackBookingErrorBeforeRedirect(
    davinciCode: number,
    errorText: string,
    redirectCallback: EventCallback
  ): void {
    // Fire add_payment_info - user completed payment step before submit failed
    trackAddPaymentInfo();

    const store = useCheckoutStore();
    const event = createBookingErrorEvent({
      session: store.sessionId,
      davinciCode,
      errorText,
      params: buildCheckoutParameters(),
    });
    pushEventBeforeRedirect(event, redirectCallback, 2000);
  }

  /**
   * Event 13: Holiday selected -> holiday_selected
   */
  function trackHolidaySelected(dateRange: DateRange): void {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    pushEvent(
      createHolidaySelectedEvent({
        date_from: localDateOrUndefined(dateRange[0]),
        date_to: localDateOrUndefined(dateRange[1]),
        duration: nightsInRange(dateRange) || undefined,
      })
    );
  }

  return {
    // SRP
    trackSrpViewed,

    // PDP
    trackPdpViewed,

    // DaVinci BA Check
    trackBookingCheckSuccess,
    trackBookingCheckFail,

    // Checkout steps
    trackCheckoutEntered,
    trackCheckoutInsuranceStep,
    trackCheckoutDataStep,
    trackCheckoutPaymentStep,
    trackAddPaymentInfo,

    // Purchase & Error (with redirect delay)
    trackPurchaseBeforeRedirect,
    trackBookingErrorBeforeRedirect,
    trackHolidaySelected,

    // Exposed for testing / advanced usage
    buildSrpParameters,
    buildPdpParameters,
    buildCheckoutParameters,
    buildCheckoutItem,
  };
}
